import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { Figure } from '../components/case-study/Figure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { VendooTabletMock } from '../components/vendoo/VendooTabletMock';
import { CompetitiveTable } from '../components/vendoo/CompetitiveTable';
import { CsvExportMock } from '../components/vendoo/CsvExportMock';
import { AnalyticsDashboardMock } from '../components/vendoo/AnalyticsDashboardMock';
import { MetricDefinitionsMock } from '../components/vendoo/MetricDefinitionsMock';
import { DesignSystemMock } from '../components/vendoo/DesignSystemMock';
import { analyticsNeeds, vendooChallenges, vendooMeta, vendooPersonas, vendooResults } from '../data/vendoo';
export function VendooCaseStudy() {
  return <CaseStudyShell data-id="element-846" data-mp="13gcdwtr3ve3o">
      <CaseHeader overline="Case study · SaaS marketplace" title="Vendoo: an analytics dashboard resellers could do taxes with" intro="I was the only designer at Vendoo, a cross-listing app for US resellers. There was no design process and few metrics, so I built both, then used them to ship CSV export, a business analytics dashboard and a design system." meta={vendooMeta} data-id="element-847" data-mp="13hx8gjdf6x1j" />

      <Figure className="mt-14" data-id="element-848" data-mp="kotcgrcqc83r">
        <VendooTabletMock data-id="element-849" data-mp="ph4a1njzzht5" />
      </Figure>

      <CaseSection index="01" label="Context" title="One listing, six marketplaces" intro={<>
            <p data-id="element-851" data-mp="aq3tev93khlx">
              Vendoo lets resellers list an item once and post it to eBay, Poshmark, Mercari, Etsy, Tradesy and Kidizen,
              instead of rebuilding the same listing by hand on each one.
            </p>
            <p data-id="element-852" data-mp="5svhvdigfbtn">
              I owned the whole product design process: feature definition, user interviews, usability and A/B testing,
              visual design, the design system, and a gap analysis of the platform when I joined.
            </p>
          </>} data-id="element-850" data-mp="pi5wspadlsqp" />

      <CaseSection index="02" label="The challenges" title="Happy users are hard to learn from" data-id="element-853" data-mp="23n90vi2gt35v">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-854" data-mp="1ynmotbydhsyn">
          {vendooChallenges.map((c, i) => <Reveal key={c.title} delay={i * 0.05} className="h-full" data-id="element-855" data-mp="2remya2fh3oxp" data-mpi={i}>
              <div className="h-full rounded-2xl bg-surface p-5" data-id="element-856" data-mp="2rck73f2l3pre">
                <h3 className="text-sm font-semibold text-fg" data-id="element-857" data-mp="2mgla5336wzfd">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-858" data-mp="2hfxlgo2fouob">{c.body}</p>
              </div>
            </Reveal>)}
        </div>
      </CaseSection>

      <CaseSection index="03" label="Building the foundation" title="Who we were building for, and what was broken" data-id="element-859" data-mp="2dh7yq4via0s0">
        <p className="-mt-2 max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-860" data-mp="11zyidr7oboux">
          Working with the founders, I defined user personas, prioritized features, mapped the user journey and aligned on
          business goals. A heuristic evaluation of the platform then gave us a list of fixes to work through.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-861" data-mp="3mmp3ls4lpvhd">
          {vendooPersonas.map((p, i) => <Reveal key={p.type} delay={i * 0.05} className="h-full" data-id="element-862" data-mp="3rfi6eagietqt" data-mpi={i}>
              <article className="flex h-full flex-col rounded-2xl border border-line bg-white p-5" data-id="element-863" data-mp="8oxekfh5qbq">
                <div className="flex items-center gap-3" data-id="element-864" data-mp="56qqzzaj181g">
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-coral-soft text-sm font-semibold text-rust" data-id="element-865" data-mp="59czc158rrkq">
                    {p.name.charAt(0)}
                  </span>
                  <div data-id="element-866" data-mp="e75caynhqn4y">
                    <p className="text-xs font-medium text-rust" data-id="element-867" data-mp="2b08bwka0se1o">{p.type}</p>
                    <h3 className="text-sm font-semibold text-fg" data-id="element-868" data-mp="2b2bb9ux2bjj2">{p.name}</h3>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-fg-muted" data-id="element-869" data-mp="1kp4vbpp2o62h">{p.description}</p>
                <p className="mt-auto border-t border-line pt-3 text-xs leading-relaxed text-fg" data-id="element-870" data-mp="1fq21z2igfoac">
                  <span className="font-semibold" data-id="element-871" data-mp="1unawu66yva6t">Needs: </span>
                  {p.needs}
                </p>
              </article>
            </Reveal>)}
        </div>
        <Figure className="mt-6" caption="Competitive analysis. Nobody turned sales data into something sellers could use at tax time — that became the opening." data-id="element-872" data-mp="1pm52k3xnrev6">
          <CompetitiveTable data-id="element-873" data-mp="x1dj0m3j7t17" />
        </Figure>
      </CaseSection>

      <CaseSection index="04" label="First feature" title="Export to CSV" intro={<p data-id="element-875" data-mp="16wyiodl4oh7s">
            Resellers were tracking inventory, sales and profit in other tools. Interviews and usability tests showed they
            needed to get their inventory out of Vendoo, so the first full feature I shipped was a CSV export.
          </p>} data-id="element-874" data-mp="1bpi5801smmoq">
        <Figure caption="The export flow: pick your columns, get a file. Try it." data-id="element-876" data-mp="2pw4tuvmkmubh">
          <CsvExportMock data-id="element-877" data-mp="2unmshd14gita" />
        </Figure>
      </CaseSection>

      <CaseSection index="05" label="Business analytics" title="A dashboard sellers could do their taxes with" data-id="element-878" data-mp="2up7nf3rcm3ro">
        <p className="-mt-2 max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-879" data-mp="2kxbbaosfgd4g">
          Analytics was a key item on the roadmap. The team adopted behavior-driven development and set a clear position:
          Vendoo would be the best tool for sellers' inventory management, sales data for taxes, and cross-listing.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-880" data-mp="2zmzc7owp59rn">
          {analyticsNeeds.map(n => <div key={n.title} className="border-t border-line pt-4" data-id="element-881" data-mp="33n1fu3lzsd9k">
              <h3 className="text-sm font-semibold text-fg" data-id="element-882" data-mp="1o72dj8z23bh8">{n.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-fg-muted" data-id="element-883" data-mp="1o95dakq0giz5">{n.body}</p>
            </div>)}
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-id="element-884" data-mp="3uv1ndv8iaaj8">
          <Figure className="h-full" caption="What we measured, and exactly how." data-id="element-885" data-mp="3uursbt8ltd6o">
            <MetricDefinitionsMock data-id="element-886" data-mp="3pxja5sngh95f" />
          </Figure>
          <Figure className="h-full" caption="The dashboard. Switch the range to see it update." data-id="element-887" data-mp="3l58c6vc9zxfi">
            <AnalyticsDashboardMock data-id="element-888" data-mp="3g6oooqi1adl1" />
          </Figure>
        </div>
      </CaseSection>

      <CaseSection index="06" label="Design system" title="One component file for design and dev" intro={<p data-id="element-890" data-mp="374p5psngkzfg">
            I built a Figma component library to cover both design and development needs, and the developers adopted
            Storybook to mirror it in code.
          </p>} data-id="element-889" data-mp="3c4toxqfr489d">
        <Figure caption="A slice of the component library." data-id="element-891" data-mp="2x8m2skks8wrg">
          <DesignSystemMock data-id="element-892" data-mp="10f0tp0mkyv4v" />
        </Figure>
      </CaseSection>

      <CaseSection index="07" label="Results" title="Revenue grew every month" data-id="element-893" data-mp="10ehjk6369b1e">
        <div className="grid gap-3 md:grid-cols-3" data-id="element-894" data-mp="vgpjgosdiim4">
          {vendooResults.map((r, i) => <Reveal key={r.value} delay={i * 0.05} className="h-full" data-id="element-895" data-mp="13wf3puwpuqis" data-mpi={i}>
              <div className={`h-full rounded-2xl p-5 ${i === 0 ? 'bg-ink text-white' : 'bg-surface'}`} data-id="element-896" data-mp="17y0bj1ue8em4">
                <p className={`text-2xl font-semibold tracking-tight ${i === 0 ? 'text-white' : 'text-ink'}`} data-id="element-897" data-mp="1cp8lq03js40c">{r.value}</p>
                <p className={`mt-1.5 text-sm leading-relaxed ${i === 0 ? 'text-white/75' : 'text-fg-muted'}`} data-id="element-898" data-mp="1ho3dvsnen4x4">{r.label}</p>
              </div>
            </Reveal>)}
        </div>
      </CaseSection>

      <NextCase title="Palacio de Hierro: luxury shopping and credit in one app" href="/work/palacio-de-hierro" data-id="element-899" data-mp="p07kawt4tt4k" />
    </CaseStudyShell>;
}